import { RecordOf } from '@opengov/compose-app/immutable';
import { List } from 'immutable';
import { IReportCreator } from '@opengov/api-deprecated';
import {
  EReportCreatedByFitler,
  EReportSortOption,
  EReportViewOption,
  EReportVisibilityFilter,
  IReportGalleryFilters,
  IReportGalleryPagination,
  IReportResponse,
  ReportSelectFilters,
  IReportDatasetLens
} from '@opengov/reporting-types';

import { IReportCreatePayload } from '../types';

export const Report = RecordOf<IReportResponse>({
  id: null,
  title: null,
  description: null,
  createdAt: null,
  deletedAt: null,
  updatedAt: null,
  viewId: null,
  lensId: null,
  updatedBy: null,
  createdBy: null,
  createdByUser: null,
  isDeleted: null,
  entityId: null,
  defaultReportConfiguration: null,
  visibility: null,
  isDimensional: null,
  isMultiDataset: null,
  allowedActions: null,
  datasetIds: null,
  reportConfigurationId: null,
  savedViewId: null,
  parentFolderId: null,
  isReadOnly: false,
  defaultSavedViewId: null,
  // Immutable records drop any key absent from these defaults, so a field missing here is
  // discarded from every loaded report without an error. `entityGroupId` decides which entities'
  // chart of accounts a combined report resolves against, and dropping it makes a saved combined
  // report silently read the single-entity hierarchy.
  entityGroupId: null
});

export const ReportCreatePayload = RecordOf<IReportCreatePayload>({
  title: null,
  description: null,
  viewId: null,
  lensId: null,
  entityId: null,
  isDimensional: null,
  isMultiDataset: null,
  defaultReportConfiguration: null,
  parentFolderId: null
});

export const ReportGalleryDefaultFilter: IReportGalleryFilters = {
  search: null,
  createdBy: EReportCreatedByFitler.ANYONE,
  visibility: EReportVisibilityFilter.ALL,
  sort: EReportSortOption.LAST_UPDATED_NEWEST,
  view: EReportViewOption.LIST,
  isLoaded: false
};

export const ReportGalleryFilters = RecordOf<IReportGalleryFilters>(ReportGalleryDefaultFilter);

export const ReportGalleryPagination = RecordOf<IReportGalleryPagination>({
  current: { limit: 25, offset: 0 },
  meta: {
    total: 1, // When the totalCount is 0, the Page number in the Pagination Component is not selected.
    next: { limit: 25, offset: 25 },
    prev: { limit: 25, offset: 0 }
  }
});

export const ReportCreatedByOptions = List<ReportSelectFilters>([
  { key: EReportCreatedByFitler.ANYONE, label: 'Anyone', default: true },
  { key: EReportCreatedByFitler.ME, label: 'Me', default: false },
  { key: EReportCreatedByFitler.NOT_ME, label: 'Not me', default: false }
]);

export const ReportVisibilityOptions = List<ReportSelectFilters>([
  { key: EReportVisibilityFilter.ALL, label: 'All', default: true },
  { key: EReportVisibilityFilter.INTERNAL, label: 'Internal', default: false },
  { key: EReportVisibilityFilter.PUBLIC, label: 'Public', default: false }
]);

export const ReportSortOptions = List<ReportSelectFilters>([
  { key: EReportSortOption.LAST_UPDATED_NEWEST, label: 'Last Updated: Newest', default: true },
  { key: EReportSortOption.LAST_UPDATED_OLDEST, label: 'Last Updated: Oldest', default: false },
  { key: EReportSortOption.NAME_A_Z, label: 'Name: A to Z', default: false },
  { key: EReportSortOption.NAME_Z_A, label: 'Name: Z to A', default: false }
]);

export const ReportCreator = RecordOf<IReportCreator>({
  full_name: null,
  uuid: null
} as IReportCreator);

export const ReportDatasetLens = RecordOf<IReportDatasetLens>({
  id: null,
  entityId: null,
  name: null,
  description: null,
  sourceId: null,
  filters: null,
  columns: null,
  createdAt: null,
  updatedAt: null,
  dataUpdatedAt: null,
  standardType: null,
  managed: null
});
